import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { toast } from 'react-toastify'
import { useLocation, useNavigate } from 'react-router-dom'
import { handleIsFlourRoute, handleIsSettingsRoute } from 'utils'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'store'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import {
  Collapse,
  IconButton,
  List,
  ListItemText,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined'
import ArrowCircleLeftOutlinedIcon from '@mui/icons-material/ArrowCircleLeftOutlined'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import LockIcon from '@mui/icons-material/Lock'
import { onLogout, useMediaService } from 'services'
import { successToast } from 'utils/toastService'
import { getSidebarRoutes, RouteConfig } from '../../../routes'
import {
  toggleSideNav,
  setOpenSideNav,
  setCloseSideNav,
  setClickOnNestedIcon,
  setClickOnRegularIcon,
  setCloseSideNavFromNested,
} from 'store/slices/sideNavSlice'
import { resetProfileOnSignOut } from 'store/slices/userSlice'
import MainLogoIcon from 'assets/svgComponents/MainLogoIcon'
import DownArrow from 'assets/svgComponents/DownArrow'
import { usePermissions } from '../../../utils/hooks'
import { RouteKeys, SVGComponent } from '../../../types'
import {
  HeaderText,
  IconContainer,
  IconsItemsList,
  ItemsList,
  LogoContainer,
  MenuTitle,
  NestedContainer,
  ShrinkedSitesContainer,
  SitesContainer,
  StyledAvatar,
  StyledLink,
  StyledMenu,
  SubRouteLink,
  UsersData,
  UserSection,
} from './SideNav.styled'
import useUserInfoModal from './dialogs/InfoUserModal/useUserInfoModal'
import { CompanyDropdown } from './components/CompanyDropdown'
import { CompanyRole } from '../../../types'
import * as sxStyles from './SideNav.style'
import { LogoTag } from '../../../components'
import { EnvBasedStyles, getMode } from '../../../utils/env'

export function filterRoutes(
  routes: RouteConfig[],
  checkPermission: Function,
  checkAnyPermission: Function,
  publicSettings: Record<string, boolean | string | number>
): RouteConfig[] {
  if (!routes || !Array.isArray(routes) || routes.length === 0) {
    console.warn(
      'No routes provided to filterRoutes or routes is not an array',
      routes
    )
    return []
  }

  return routes
    .map((route) => {
      if (!route) return null
      if (route?.settings && Object.keys(publicSettings).length > 0) {
        const allSettingsEnabled = route.settings.every((setting: string) => {
          const settingValue = publicSettings[setting]

          return settingValue === true
        })

        // If any setting is not true, filter out this route
        if (!allSettingsEnabled) {
          return null
        }
      }

      let hasPermission = !route.permissions

      if (route.permissions) {
        const { entity, action } = route.permissions

        if (Array.isArray(action)) {
          hasPermission = checkAnyPermission(entity, action)
        } else if (action) {
          hasPermission = checkPermission(entity, action)
        } else {
          hasPermission = true
        }
      }

      const filteredChildren = route.children
        ? filterRoutes(
            route.children,
            checkPermission,
            checkAnyPermission,
            publicSettings
          )
        : undefined

      if (hasPermission || (filteredChildren && filteredChildren.length > 0)) {
        return { ...route, children: filteredChildren }
      }

      return null
    })
    .filter(Boolean) as RouteConfig[]
}

const getCompanyOccurrences = (companyRoles: CompanyRole[]) => {
  const companyOccurrences = {}

  companyRoles.forEach((item) => {
    const companyId = item.company_id
    companyOccurrences[companyId] = (companyOccurrences[companyId] || 0) + 1
  })

  return companyOccurrences
}

export default function SideNav() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const theme = useTheme()
  const { pathname } = useLocation()
  const { checkPermission, checkAnyPermission } = usePermissions()
  const userRef = useRef<HTMLDivElement>(null)
  const sitesRef = useRef<HTMLDivElement>(null)

  const user = useSelector((state: RootState) => state?.user?.profile)
  const { isSideNavOpen, responsiveResizingDisabled } = useSelector(
    (state: RootState) => state.sideNav
  )
  const currentCompany = useSelector(
    (state: RootState) => state?.user?.currentCompany
  )

  const publicSettings = useSelector(
    (state: RootState) => state?.user?.settings || {}
  )

  const { downloadMediaFileSrc } = useMediaService()

  const { data: media } = useQuery(
    ['fetch-curr-company-logo', currentCompany?.icon],
    () =>
      currentCompany?.icon ? downloadMediaFileSrc(currentCompany?.icon) : null,
    {
      enabled: !!currentCompany?.icon,
    }
  )

  const onLogoutMutation = useMutation(onLogout, {
    onSuccess: () => {
      successToast('Loged out successfully')
      localStorage.removeItem('token')
      navigate('/auth/login')
      dispatch(resetProfileOnSignOut())
    },
    onError: () => {
      toast.error('An error occurred while trying to logout')
    },
  })

  const [renderUserInfoModal, toggleUserInfoModal] = useUserInfoModal({})
  const [isUserMenuOpen, setIsUserMenuOpen] = useState<boolean>(false)
  const [isSitesMenuOpen, setIsSitesMenuOpen] = useState<boolean>(false)

  const sidebarRoutes = getSidebarRoutes(publicSettings)

  const filteredRoutes = filterRoutes(
    sidebarRoutes,
    checkPermission,
    checkAnyPermission,
    publicSettings
  )

  const displayRoutes =
    filteredRoutes.length > 0 ? filteredRoutes : sidebarRoutes

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'))

  useEffect(() => {
    if (!responsiveResizingDisabled) {
      if (isSmallScreen) {
        dispatch(setCloseSideNav())
      } else {
        dispatch(setOpenSideNav())
      }
    }
  }, [dispatch, isSmallScreen, responsiveResizingDisabled])

  const handleToggleSideNav = () => {
    dispatch(toggleSideNav())
  }

  const handleUserMenuClose = () => {
    setIsUserMenuOpen(false)
  }
  const handleUserMenuOpen = () => {
    setIsUserMenuOpen(true)
  }

  const handleSitesMenuClose = () => {
    setIsSitesMenuOpen(false)
  }

  const handleSitesMenuOpen = () => {
    setIsSitesMenuOpen(true)
  }

  const handleLogOut = () => {
    onLogoutMutation.mutate()
  }

  const isSettingsActive = useMemo(
    () => handleIsSettingsRoute(pathname),
    [pathname]
  )

  const envBasedStyling = EnvBasedStyles[getMode()]

  const isFloursActive = useMemo(() => handleIsFlourRoute(pathname), [pathname])

  const settings = useSelector((state: RootState) => state.user.settings)

  const renderSideNavHeader = () => {
    return (
      <Box
        sx={
          isSideNavOpen ? sxStyles.navHeaderBox : sxStyles.shrinkedNavHeaderBox
        }
      >
        <Box sx={sxStyles.navLogoBox}>
          <LogoContainer background={envBasedStyling.color}>
            <MainLogoIcon color={envBasedStyling.logoColor} />
          </LogoContainer>
        </Box>
        {isSideNavOpen ? (
          <Box sx={sxStyles.navTitleBox}>
            <HeaderText component="h1">Manna</HeaderText>
            <HeaderText component="h1" fontWeight="700">
              {settings?.SHOW_BAKER_MODULE ? 'Bakery' : 'Wheat'}
            </HeaderText>
            {envBasedStyling.label && (
              <LogoTag
                label={envBasedStyling.label}
                color={envBasedStyling.color}
              />
            )}
          </Box>
        ) : (
          <></>
        )}
        <Box sx={sxStyles.resizeBtnBox}>
          <IconButton
            sx={isSideNavOpen ? sxStyles.wideIconBtn : sxStyles.shrinkIconBtn}
            onClick={handleToggleSideNav}
          >
            {isSideNavOpen ? (
              <ArrowCircleLeftOutlinedIcon />
            ) : (
              <ArrowCircleRightOutlinedIcon />
            )}
          </IconButton>
        </Box>
      </Box>
    )
  }

  const renderShrinkedRegularIcon = (
    label: string,
    key: string,
    Icon: SVGComponent,
    notAvailable: boolean
  ) => {
    return (
      <Tooltip
        key={key}
        title={notAvailable ? `${label} - coming soon` : label}
        placement="left"
      >
        <IconContainer
          key={key}
          to={key}
          isActive={pathname === `/${key}`}
          onClick={() => dispatch(setClickOnRegularIcon())}
        >
          <Icon
            fill={pathname === `/${key}` ? theme.palette.primary.dark : 'white'}
          />
        </IconContainer>
      </Tooltip>
    )
  }

  const renderRegularIcon = (
    label: string,
    key: string,
    Icon: SVGComponent,
    active: boolean,
    isSetting?: boolean,
    onSettingPath?: boolean,
    subRoutesCount?: number
  ) => {
    return (
      <Tooltip key={key} title={label} placement="left">
        <IconContainer
          key={key}
          to={key}
          isActive={active}
          isSettingsOpen={isSetting}
          isSideNavOpen={isSideNavOpen}
          subRoutesCount={subRoutesCount}
          onClick={() => {
            onSettingPath !== undefined
              ? dispatch(setClickOnNestedIcon())
              : dispatch(setClickOnRegularIcon())
          }}
        >
          <Icon fill={active ? theme.palette.primary.dark : 'white'} />
        </IconContainer>
      </Tooltip>
    )
  }

  return (
    <Box
      sx={
        isSideNavOpen
          ? sxStyles.sideNavWideMainBox
          : sxStyles.sideNavShrinkedMainBox
      }
    >
      {renderSideNavHeader()}
      {/* body of the sideNav */}
      <Box
        sx={
          isSideNavOpen
            ? sxStyles.sideNavBodyBox
            : sxStyles.sideNavShrinkedBodyBox
        }
      >
        <IconsItemsList
          sx={
            isSideNavOpen
              ? sxStyles.sideNavBodyBox
              : sxStyles.sideNavShrinkedBodyBox
          }
        >
          {displayRoutes.map(
            ({ label, key, icon: Icon, children, notAvailable }) => {
              if (handleIsSettingsRoute(`/${key}`)) {
                return renderRegularIcon(
                  label,
                  key,
                  Icon,
                  isSettingsActive,
                  isSettingsActive,
                  true,
                  children?.length || 0
                )
              } else if (
                key === RouteKeys.Flours ||
                handleIsFlourRoute(`/${key}`)
              ) {
                return renderRegularIcon(
                  label,
                  key,
                  Icon,
                  isFloursActive,
                  isFloursActive,
                  true,
                  children?.length || 0
                )
              }
              return isSideNavOpen
                ? renderRegularIcon(label, key, Icon, pathname === `/${key}`)
                : renderShrinkedRegularIcon(label, key, Icon, notAvailable)
            }
          )}

          <UserSection>
            <StyledAvatar
              alt=""
              src={media}
              onClick={handleSitesMenuOpen}
              sx={sxStyles.companyAvatarStyle}
            />
            {/* Image for user */}
            <StyledAvatar alt="" onClick={handleUserMenuOpen} />
          </UserSection>
        </IconsItemsList>

        <ItemsList sx={sxStyles.secondItemsList}>
          {isSideNavOpen ? (
            filteredRoutes.map(
              ({ label, key, children, notAvailable, path }) => {
                // NOTE: maybe worth to put stings into enum
                if (handleIsSettingsRoute(`/${key}`)) {
                  return (
                    <NestedContainer key={key}>
                      <StyledLink
                        to={key}
                        isActive={isSettingsActive}
                        onClick={() => dispatch(setClickOnNestedIcon())}
                      >
                        {label}
                      </StyledLink>
                      <Collapse
                        in={isSettingsActive}
                        timeout="auto"
                        unmountOnExit
                      >
                        <List component="div" disablePadding>
                          {children?.map(({ key, label, notAvailable }) =>
                            // lock flour module in the future remove this condition and leave the first section
                            notAvailable ? (
                              <ListItemText key={key}>
                                {
                                  <SubRouteLink
                                    to={key}
                                    isActive={pathname === `/${key}`}
                                    onClick={() =>
                                      dispatch(setCloseSideNavFromNested())
                                    }
                                  >
                                    <ChevronRightIcon fontSize="small" />
                                    {label}
                                    <LockIcon
                                      fontSize="small"
                                      sx={sxStyles.lockIcon}
                                    />
                                  </SubRouteLink>
                                }
                              </ListItemText>
                            ) : (
                              <ListItemText key={key}>
                                {
                                  <SubRouteLink
                                    to={key}
                                    isActive={pathname === `/${key}`}
                                    onClick={() =>
                                      dispatch(setCloseSideNavFromNested())
                                    }
                                  >
                                    <ChevronRightIcon fontSize="small" />
                                    {label}
                                  </SubRouteLink>
                                }
                              </ListItemText>
                            )
                          )}
                        </List>
                      </Collapse>
                    </NestedContainer>
                  )
                }

                if (key === RouteKeys.Flours || handleIsFlourRoute(`/${key}`)) {
                  return (
                    <NestedContainer key={key}>
                      <StyledLink
                        to={key}
                        isActive={isFloursActive}
                        onClick={() => dispatch(setClickOnNestedIcon())}
                      >
                        {label}
                      </StyledLink>
                      <Collapse
                        in={isFloursActive}
                        timeout="auto"
                        unmountOnExit
                      >
                        <List component="div" disablePadding>
                          {children?.map(
                            ({ key, label, notAvailable, path }) => (
                              <ListItemText key={key}>
                                {
                                  <SubRouteLink
                                    to={key}
                                    isActive={pathname.includes(path)}
                                    onClick={() =>
                                      dispatch(setCloseSideNavFromNested())
                                    }
                                  >
                                    <ChevronRightIcon fontSize="small" />
                                    {label}
                                    {notAvailable && (
                                      <LockIcon
                                        fontSize="small"
                                        sx={sxStyles.lockIcon}
                                      />
                                    )}
                                  </SubRouteLink>
                                }
                              </ListItemText>
                            )
                          )}
                        </List>
                      </Collapse>
                    </NestedContainer>
                  )
                }

                return notAvailable ? (
                  <Tooltip key={key} title={'coming soon'} placement="left">
                    <StyledLink
                      key={key}
                      to={key}
                      isActive={pathname === `/${key}`}
                      onClick={() => dispatch(setClickOnRegularIcon())}
                    >
                      <Box sx={sxStyles.boxWrappingLockIcon}>
                        {label}
                        <LockIcon fontSize="small" sx={sxStyles.lockIcon} />
                      </Box>
                    </StyledLink>
                  </Tooltip>
                ) : (
                  <StyledLink
                    key={key}
                    to={key}
                    isActive={pathname === `/${key}`}
                    onClick={() => dispatch(setClickOnRegularIcon())}
                  >
                    {label}
                  </StyledLink>
                )
              }
            )
          ) : (
            <></>
          )}
          <UserSection>
            {isSideNavOpen ? (
              <SitesContainer ref={sitesRef} onClick={handleSitesMenuOpen}>
                <Box>
                  <Typography sx={sxStyles.menuOptionsTypography}>
                    {currentCompany?.name}
                  </Typography>
                </Box>
                <Box>
                  <DownArrow />
                </Box>
              </SitesContainer>
            ) : (
              <ShrinkedSitesContainer ref={sitesRef} />
            )}
            <CompanyDropdown
              user={user}
              anchorEl={sitesRef?.current}
              handleSitesMenuClose={handleSitesMenuClose}
              isSitesMenuOpen={isSitesMenuOpen}
              getCompanyOccurrences={getCompanyOccurrences}
            />
            {isSideNavOpen ? (
              <UsersData onClick={handleUserMenuOpen} ref={userRef}>
                <Typography sx={sxStyles.userNameMenuTypography}>
                  {user?.name}
                </Typography>
                <Typography sx={sxStyles.userEmailMenuTypography}>
                  {user?.email}
                </Typography>
              </UsersData>
            ) : (
              <></>
            )}

            <StyledMenu
              id="users-menu"
              anchorEl={userRef?.current}
              open={isUserMenuOpen}
              onClose={handleUserMenuClose}
            >
              <MenuTitle>
                <Typography sx={sxStyles.titleTypographyStyle}>
                  {user?.name} {user?.surname}
                </Typography>
              </MenuTitle>

              <MenuItem
                onClick={() => {
                  toggleUserInfoModal()
                  handleUserMenuClose()
                }}
                disableRipple
              >
                <Typography sx={sxStyles.typographyStyle}>
                  My profile
                </Typography>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleUserMenuClose()
                  handleLogOut()
                }}
                disableRipple
              >
                <Typography sx={sxStyles.typographyStyle}>Sign out</Typography>
              </MenuItem>
            </StyledMenu>
          </UserSection>
        </ItemsList>
      </Box>
      {renderUserInfoModal}
    </Box>
  )
}
